import { Fragment, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface SplitChipMenuItem {
  value: string;
  label: ReactNode;
  count?: number;
}

/**
 * Radio menus pick exactly one mode ('value' may sit outside the items, e.g.
 * 'all' = filter off); checkbox menus toggle each item independently.
 */
export type SplitChipMenu =
  | {
      type: 'radio';
      value: string;
      items: SplitChipMenuItem[];
      onChange: (value: string) => void;
    }
  | {
      type: 'checkbox';
      checked: ReadonlySet<string>;
      /** Flat checkbox list; ignored when `sections` is given */
      items?: SplitChipMenuItem[];
      /** Alternative to `items`: checkboxes grouped under headings,
       *  separator-divided (values must be unique ACROSS sections) */
      sections?: { heading: ReactNode; items: SplitChipMenuItem[] }[];
      onChange: (value: string, checked: boolean) => void;
      /** Optional leading "All" row — checked while the set is empty (= no
       *  filtering), and selecting it clears the whole set. */
      allItem?: { label: ReactNode; count?: number; onSelect: () => void };
    };

interface SplitChipProps {
  /** Main-segment text, count included */
  label: string;
  active: boolean;
  onToggle: () => void;
  title?: string;
  menuAriaLabel: string;
  menu: SplitChipMenu;
}

/**
 * Split filter chip: the left segment toggles the filter with its current
 * mode; the chevron segment opens a menu of modes (radio or checkboxes).
 */
export default function SplitChip({
  label,
  active,
  onToggle,
  title,
  menuAriaLabel,
  menu,
}: SplitChipProps) {
  const count = (item: { count?: number }) =>
    item.count !== undefined && (
      <span className="ml-auto pl-3 text-xs text-muted-foreground">
        {item.count}
      </span>
    );

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-stretch overflow-hidden whitespace-nowrap rounded-full border text-xs font-medium transition-colors',
        active
          ? 'border-transparent bg-primary text-primary-foreground shadow-sm'
          : 'border-border text-muted-foreground'
      )}
    >
      <button
        onClick={onToggle}
        aria-pressed={active}
        title={title}
        className={cn(
          'py-1 pl-2.5 pr-2 transition-colors',
          active ? 'hover:bg-primary/90' : 'hover:text-foreground'
        )}
      >
        {label}
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            aria-label={menuAriaLabel}
            className={cn(
              'inline-flex items-center border-l py-1 pl-1 pr-1.5 transition-colors',
              active
                ? 'border-primary-foreground/30 hover:bg-primary/90'
                : 'border-border hover:text-foreground'
            )}
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
          {menu.type === 'radio' ? (
            <DropdownMenuRadioGroup
              value={menu.value}
              onValueChange={menu.onChange}
            >
              {menu.items.map(item => (
                <DropdownMenuRadioItem key={item.value} value={item.value}>
                  {item.label}
                  {count(item)}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          ) : (
            <>
              {menu.allItem && (
                <>
                  <DropdownMenuCheckboxItem
                    checked={menu.checked.size === 0}
                    onCheckedChange={menu.allItem.onSelect}
                    onSelect={e => e.preventDefault()}
                  >
                    {menu.allItem.label}
                    {count(menu.allItem)}
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator />
                </>
              )}
              {(
                menu.sections ?? [{ heading: null, items: menu.items ?? [] }]
              ).map((section, i) => (
                // Section order is fixed per menu — index keys are stable
                <Fragment key={i}>
                  {i > 0 && <DropdownMenuSeparator />}
                  {section.heading != null && (
                    <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                      {section.heading}
                    </DropdownMenuLabel>
                  )}
                  {section.items.map(item => (
                    <DropdownMenuCheckboxItem
                      key={item.value}
                      checked={menu.checked.has(item.value)}
                      onCheckedChange={checked =>
                        menu.onChange(item.value, checked)
                      }
                      // Keep the menu open while ticking several boxes
                      onSelect={e => e.preventDefault()}
                    >
                      {item.label}
                      {count(item)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </Fragment>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
